import React, { useState } from 'react';
import { Users, Plus, Edit2, Trash2, Save, X, AlertCircle, BookOpen } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

const TeachersSection = ({ 
  formData, 
  handleInputChange, 
  handleArrayChange,
  errors,
  subjects
}) => {
  // Состояние формы для добавления/редактирования преподавателя
  const [teacherForm, setTeacherForm] = useState({
    id: null,
    name: '',
    subjects: [],
    availability: []
  });
  
  // Флаг, показывающий, находимся ли мы в режиме редактирования
  const [isEditing, setIsEditing] = useState(false);
  
  // Функция сброса формы
  const resetForm = () => {
    setTeacherForm({
      id: null,
      name: '',
      subjects: [],
      availability: []
    });
    setIsEditing(false);
  };
  
  // Функция для обработки изменений в форме преподавателя
  const handleTeacherFormChange = (e) => {
    const { name, value } = e.target;
    setTeacherForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Функция для обработки выбора предметов
  const handleSubjectsChange = (subjectId) => {
    setTeacherForm(prev => {
      const subjects = [...prev.subjects];
      
      if (subjects.includes(subjectId)) {
        // Удаляем предмет если он уже выбран
        return {
          ...prev,
          subjects: subjects.filter(id => id !== subjectId)
        };
      } else {
        // Добавляем новый предмет
        return {
          ...prev,
          subjects: [...subjects, subjectId]
        };
      }
    });
  };
  
  // Функция для добавления/обновления преподавателя
  const handleSubmitTeacher = (e) => {
    e.preventDefault();
    
    // Проверка заполнения имени
    if (!teacherForm.name.trim()) {
      alert('Пожалуйста, введите ФИО преподавателя');
      return;
    }
    
    // Проверка, выбран ли хотя бы один предмет
    if (teacherForm.subjects.length === 0) {
      alert('Пожалуйста, выберите хотя бы один предмет, который ведет преподаватель');
      return;
    }
    
    if (isEditing) {
      // Обновление существующего преподавателя
      handleArrayChange('teachers', 'update', teacherForm);
    } else {
      // Добавление нового преподавателя
      handleArrayChange('teachers', 'add', teacherForm);
    }
    
    // Сброс формы
    resetForm();
  };
  
  // Функция для начала редактирования преподавателя
  const startEditingTeacher = (teacher) => {
    setTeacherForm({
      id: teacher.id,
      name: teacher.name,
      subjects: [...teacher.subjects],
      availability: [...(teacher.availability || [])]
    });
    setIsEditing(true);
  };
  
  // Функция для удаления преподавателя
  const deleteTeacher = (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этого преподавателя?')) {
      handleArrayChange('teachers', 'remove', { id });
    }
  };
  
  // Функция для получения названия предмета по ID
  const getSubjectName = (subjectId) => {
    const subject = formData.subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Неизвестный предмет';
  };
  
  // Функция для получения цвета предмета по ID
  const getSubjectColor = (subjectId) => {
    const subject = formData.subjects.find(s => s.id === subjectId);
    return subject ? subject.color : '#cccccc';
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium text-gray-900 dark:text-white flex items-center">
        <Users className="w-5 h-5 mr-2 text-indigo-500" />
        Преподаватели
      </h3>
      
      {/* Форма добавления/редактирования преподавателя */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">
          {isEditing ? 'Редактирование преподавателя' : 'Добавление нового преподавателя'}
        </h4>
        
        <form onSubmit={handleSubmitTeacher} className="space-y-4">
          <div>
            <label htmlFor="teacherName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              ФИО преподавателя
            </label>
            <input
              type="text"
              id="teacherName"
              name="name"
              value={teacherForm.name}
              onChange={handleTeacherFormChange}
              className="form-input w-full"
              placeholder="Например: Иванов Иван Иванович"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <BookOpen className="w-4 h-4 mr-1" />
              Преподаваемые предметы
            </label>
            
            {formData.subjects.length === 0 ? (
              <div className="p-3 rounded-md border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Пожалуйста, сначала добавьте предметы в разделе "Предметы"
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {formData.subjects.map(subject => (
                  <label 
                    key={subject.id} 
                    className="inline-flex items-center p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={teacherForm.subjects.includes(subject.id)}
                      onChange={() => handleSubjectsChange(subject.id)}
                      className="form-checkbox"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                      <span 
                        className="w-3 h-3 rounded-full mr-1" 
                        style={{ backgroundColor: subject.color }}
                      ></span>
                      {subject.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
            
            {errors.teachersSubjects && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.teachersSubjects}
              </p>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              primary
              size="sm"
              icon={isEditing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              disabled={formData.subjects.length === 0}
            >
              {isEditing ? 'Сохранить' : 'Добавить преподавателя'}
            </Button>
            
            {isEditing && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={<X className="w-4 h-4" />}
                onClick={resetForm}
              >
                Отменить
              </Button>
            )}
          </div>
        </form>
      </div>
      
      {/* Список преподавателей */}
      <div>
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">Список преподавателей</h4>
        
        {errors.teachers && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.teachers}
          </div>
        )}
        
        {formData.teachers.length === 0 ? (
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">Преподаватели не добавлены</p>
            <Button
              type="button"
              variant="link"
              size="sm"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => document.getElementById('teacherName').focus()}
              disabled={formData.subjects.length === 0}
            >
              Добавить первого преподавателя
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {formData.teachers.map((teacher) => (
              <div 
                key={teacher.id} 
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center">
                  <div className="flex-grow">
                    <div className="font-medium text-gray-800 dark:text-gray-200">
                      {teacher.name}
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => startEditingTeacher(teacher)}
                      className="text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                      title="Редактировать"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteTeacher(teacher.id)}
                      className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {teacher.subjects && teacher.subjects.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {teacher.subjects.map(subjectId => (
                      <span 
                        key={subjectId}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ 
                          backgroundColor: `${getSubjectColor(subjectId)}20`,
                          color: getSubjectColor(subjectId)
                        }}
                      >
                        {getSubjectName(subjectId)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Важное замечание
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Согласно нормативам для российских школ и ВУЗов, максимальная учебная нагрузка на преподавателя 
              не должна превышать 36 часов в неделю. При составлении расписания система будет учитывать это ограничение, 
              а также выбранные предметы для каждого преподавателя.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeachersSection;